import { STOCK_LEVELS, VAT_PERCENTAGE } from "../config/constants";
import { AccessLevel } from "../models/enums/AccessLevel";
import _ from "lodash";
import { Theme } from "@mui/material/styles";

export const formatTimestamp = (timestampString) => {
	const timestamp = new Date(timestampString);
	const options: Intl.DateTimeFormatOptions = {
		year: "numeric",
		month: "long", // Abbreviated month
		day: "numeric",
	};
	return timestamp.toLocaleDateString("en-GB", options);
};

export const formatUpdatedTimestamp = (timestampString: string) => {
    const timestamp = new Date(timestampString);
    const now = new Date();
    const differenceInMilliseconds = now.getTime() - timestamp.getTime();
    const oneDayInMilliseconds = 24 * 60 * 60 * 1000;

    if (differenceInMilliseconds < oneDayInMilliseconds) {
        const seconds = Math.floor(differenceInMilliseconds / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);

        if (hours > 0) {
            return `${hours} hour${hours > 1 ? 's' : ''} ago`;
        } else if (minutes > 0) {
            return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
        } else {
            return `${seconds} second${seconds > 1 ? 's' : ''} ago`;
        }
    }

	return "-";
};

export const getStatusText = (status, compact = false) => {
	switch (status) {
		case 1:
			return compact ? "Pending" : "Pending Order Confirmation";
		case 2:
			return compact ? "Confirmed" : "Order Confirmed";
		case 3:
			return compact ? "In Transit" : "In Transit";
		case 4:
			return compact ? "Delivered" : "Order Delivered";
		default:
			return compact ? "Unknown" : "Unknown Status";
	}
};

export const getReferenceValue = (reference) => {
	const indexOfFirstDash = reference.indexOf("-");
	if (indexOfFirstDash !== -1) {
		return reference.substring(0, indexOfFirstDash).toString().toUpperCase();
	}
	return reference;
};

export const getDeliveryTypeSetup = (deliveryType: string, theme?: Theme | null, isLarge: boolean = false) => {
	const text = {
		"sameday": isLarge ? "Same Day Delivery" : "Same Day",
		"nextDay": isLarge ? "Next Day Delivery" : "Next Day",
		"standard": isLarge ? "Standard Delivery" : "Standard",
	}

	// Use theme colors if available, otherwise fallback to original colors
	const backgroundColor = theme ? {
		"sameday": `${theme.palette.warning.main}25`, // Using hex alpha (25 = 15% opacity)
		"nextDay": `${theme.palette.success.main}25`,
		"standard": theme.palette.mode === "dark" 
			? `${theme.palette.info.main}25` 
			: `${theme.palette.info.main}25`,
	} : {
		"sameday": "rgba(255, 152, 0, 0.1)",
		"nextDay": "rgba(0, 128, 0, 0.1)",
		"standard": "rgba(0, 0, 0, 0.1)",
	}

	const textColor = theme ? {
		"sameday": theme.palette.warning.main,
		"nextDay": theme.palette.success.main,
		"standard": theme.palette.info.main,
	} : {
		"sameday": "orange",
		"nextDay": "green",
		"standard": "black",
	}

	const borderColor = theme ? {
		"sameday": theme.palette.warning.main,
		"nextDay": theme.palette.success.main,
		"standard": theme.palette.info.main,
	} : {
		"sameday": "orange",
		"nextDay": "green",
		"standard": "black",
	}

	return {
		text: text[deliveryType],
		backgroundColor: backgroundColor[deliveryType],
		borderColor: borderColor[deliveryType],
		textColor: textColor[deliveryType],
	}
}

export const hasPortalAccess = (role: AccessLevel) => {
	return !_.isEmpty(role) && _.includes([AccessLevel.Moderator.toLowerCase(), AccessLevel.Admin.toLowerCase()], role.toLowerCase());
};

export const getOrderPaymentDetails = (orderItems, deliveryFee, vatPercentage = VAT_PERCENTAGE) => {
	const baseCost = orderItems.reduce((total, item) => total + item.product.price * item.quantity, 0);
	const vat = baseCost * (vatPercentage / 100);
	const totalCost = baseCost + vat + deliveryFee;
	return { baseCost, vat, totalCost, deliveryFee };
}

export const getBadgeText = ({ 
	inStock, 
	isOnSale, 
	discountAmount,
	discountType = 'percentage',
	currency = 'R'
}: { 
	inStock: boolean; 
	isOnSale: boolean; 
	discountAmount: number | string;
	discountType?: 'percentage' | 'value';
	currency?: string;
}) => {
	const numDiscount = Number(discountAmount) || 0;
	
	if (!inStock) {
		return {
			text: 'Out of Stock',
			textColor: 'white',
			backgroundColor: 'rgba(128, 128, 128, 1)',
		};
	}

	if (isOnSale && numDiscount > 0) {
		// Show different text based on discount type
		const badgeText = discountType === 'value' 
			? `${currency}${numDiscount} OFF`
			: `Sale -${numDiscount}%`;
		
		return {
			text: badgeText,
			textColor: 'white',
			backgroundColor: 'rgba(205, 0, 0, 1)',
		};
	}

	return {
		text: undefined,
		textColor: 'white',
		backgroundColor: 'rgba(128, 128, 128, 1)',
	};
};

export const calculateDiscountedPrice = (
	price: number, 
	discount: number | string = 0, 
	discountType: 'percentage' | 'value' = 'percentage'
) => {
	const numDiscount = Number(discount) || 0;
	if (discountType === 'value') {
		// Fixed amount discount
		return Math.max(0, price - numDiscount).toFixed(2);
	}
	// Percentage discount
	return (price - (price * (numDiscount / 100))).toFixed(2);
};

// =============================================================================
// PRODUCT FILTERING UTILITIES
// =============================================================================

/**
 * Filter products to only show active ones
 * @param products - Array of products
 */
export const getActiveProducts = (products: any[]) => {
	return products.filter((product) => product.isActive !== false);
};

/**
 * Get product count by category (only active products)
 * @param products - Array of products
 * @param category - Category name ('All' for all categories)
 */
export const getActiveProductsLength = (products: any[], category: string) => {
	const activeProducts = getActiveProducts(products);
	if (category === 'All') return activeProducts.length;
	return activeProducts.filter((product) => product.category === category).length;
};

/**
 * Filter products by category (only active products)
 * @param products - Array of products
 * @param category - Category name ('All' for all categories)
 */
export const filterProductsByCategory = (products: any[], category: string) => {
	const activeProducts = getActiveProducts(products);
	return category === 'All'
		? activeProducts
		: activeProducts.filter((product) => product.category === category);
};

/**
 * Filter products by category and optional subcategory (only active products)
 * @param products - Array of products
 * @param category - Category name ('All' for all categories)
 * @param subcategory - Optional subcategory name (null/undefined = show all subcategories)
 */
export const filterProductsByCategoryAndSubcategory = (
	products: any[],
	category: string,
	subcategory?: string | null
) => {
	const categoryFiltered = filterProductsByCategory(products, category);
	if (!subcategory) return categoryFiltered;
	return categoryFiltered.filter((product) => product.subcategory === subcategory);
};

/**
 * Get unique subcategories with product counts for a given category
 * @param products - Array of products
 * @param category - Category to derive subcategories for ('All' returns subcategories across all categories)
 */
export const getSubcategoriesForCategory = (
	products: any[],
	category: string
): { subcategory: string; totalProducts: number }[] => {
	const activeProducts = getActiveProducts(products);
	const scoped = category === 'All'
		? activeProducts
		: activeProducts.filter((p) => p.category === category);

	const counts: Record<string, number> = {};
	scoped.forEach((p) => {
		if (p.subcategory) {
			counts[p.subcategory] = (counts[p.subcategory] || 0) + 1;
		}
	});

	return Object.entries(counts)
		.map(([subcategory, totalProducts]) => ({ subcategory, totalProducts }))
		.sort((a, b) => a.subcategory.localeCompare(b.subcategory));
};

// =============================================================================
// PRODUCT ANALYTICS UTILITIES
// =============================================================================

/**
 * Calculate stock health distribution
 * @param products - Array of products
 * @param lowStockThreshold - Stock level considered "low" (default: STOCK_LEVELS.LOW_THRESHOLD)
 */
export const calculateStockHealth = (products: any[], lowStockThreshold: number = STOCK_LEVELS.LOW_THRESHOLD) => {
	const inStock = products.filter(p => (p.stockOnHand || 0) > lowStockThreshold).length;
	const lowStock = products.filter(p => (p.stockOnHand || 0) > 0 && (p.stockOnHand || 0) <= lowStockThreshold).length;
	const outOfStock = products.filter(p => (p.stockOnHand || 0) === 0).length;
	
	return {
		inStock,
		lowStock,
		outOfStock,
		total: products.length,
		healthScore: products.length > 0 ? ((inStock / products.length) * 100).toFixed(1) : '0'
	};
};

/**
 * Calculate price statistics
 * @param products - Array of products
 */
export const calculatePriceStats = (products: any[]) => {
	if (!products || products.length === 0) {
		return { highest: 0, lowest: 0, average: 0, median: 0 };
	}
	
	const prices = products.map(p => p.price || 0).filter(p => p > 0);
	const sortedPrices = [...prices].sort((a, b) => a - b);
	
	return {
		highest: Math.max(...prices),
		lowest: Math.min(...prices),
		average: prices.reduce((sum, p) => sum + p, 0) / prices.length,
		median: sortedPrices[Math.floor(sortedPrices.length / 2)] || 0
	};
};

/**
 * Calculate category performance metrics
 * @param products - Array of products
 */
export const calculateCategoryPerformance = (products: any[]) => {
	const categories = new Map<string, {
		count: number;
		totalValue: number;
		totalStock: number;
		lowStockCount: number;
	}>();
	
	products.forEach(product => {
		const cat = product.category || 'Uncategorized';
		const existing = categories.get(cat) || { count: 0, totalValue: 0, totalStock: 0, lowStockCount: 0 };
		
		existing.count += 1;
		existing.totalValue += (product.price || 0) * (product.stockOnHand || 0);
		existing.totalStock += product.stockOnHand || 0;
		if ((product.stockOnHand || 0) <= STOCK_LEVELS.LOW_THRESHOLD) {
			existing.lowStockCount += 1;
		}
		
		categories.set(cat, existing);
	});
	
	// Find highest/lowest
	let highestValueCat = { name: '', value: 0 };
	let mostProductsCat = { name: '', count: 0 };
	
	categories.forEach((stats, name) => {
		if (stats.totalValue > highestValueCat.value) {
			highestValueCat = { name, value: stats.totalValue };
		}
		if (stats.count > mostProductsCat.count) {
			mostProductsCat = { name, count: stats.count };
		}
	});
	
	return {
		categories,
		highestValueCategory: highestValueCat,
		mostProductsCategory: mostProductsCat,
		totalCategories: categories.size
	};
};

/**
 * Calculate product freshness metrics
 * @param products - Array of products
 */
export const calculateProductFreshness = (products: any[]) => {
	const now = new Date();
	const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
	const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
	
	const recentlyAdded = products.filter(p => new Date(p.created) > sevenDaysAgo).length;
	const recentlyUpdated = products.filter(p => new Date(p.updated) > sevenDaysAgo).length;
	const stale = products.filter(p => new Date(p.updated) < thirtyDaysAgo).length;
	
	return {
		recentlyAdded,
		recentlyUpdated,
		stale,
		freshness: products.length > 0 ? ((recentlyUpdated / products.length) * 100).toFixed(1) : '0'
	};
};

/**
 * Calculate variant statistics
 * @param products - Array of products
 */
export const calculateVariantStats = (products: any[]) => {
	const withVariants = products.filter(p => p.variantOptions && p.variantOptions.length > 0).length;
	const withoutVariants = products.filter(p => !p.variantOptions || p.variantOptions.length === 0).length;
	
	// Count variant types
	const variantTypes = new Map<string, number>();
	products.forEach(p => {
		if (p.variantType) {
			variantTypes.set(p.variantType, (variantTypes.get(p.variantType) || 0) + 1);
		}
	});
	
	// Find most common variant type
	let mostCommonType = { type: 'None', count: 0 };
	variantTypes.forEach((count, type) => {
		if (count > mostCommonType.count) {
			mostCommonType = { type, count };
		}
	});
	
	return {
		withVariants,
		withoutVariants,
		variantTypes,
		mostCommonType,
		variantCoverage: products.length > 0 ? ((withVariants / products.length) * 100).toFixed(1) : '0'
	};
};

/**
 * Calculate image coverage statistics
 * @param products - Array of products
 */
export const calculateImageCoverage = (products: any[]) => {
	const withImages = products.filter(p => {
		if (Array.isArray(p.images)) {
			return p.images.length > 0;
		} else if (p.images && typeof p.images === 'object') {
			return Object.keys(p.images).length > 0;
		}
		return false;
	}).length;
	
	const withoutImages = products.length - withImages;
	
	return {
		withImages,
		withoutImages,
		coverage: products.length > 0 ? ((withImages / products.length) * 100).toFixed(1) : '0',
		missingImages: withoutImages
	};
};

/**
 * Calculate profit margin percentage
 * @param price - Product selling price
 * @param costPrice - Product cost price
 * @returns Formatted profit margin string (e.g., '+35%') or null if no valid cost price
 */
export const calculateProfitMargin = (price: number, costPrice?: number | null): string | null => {
	if (!costPrice || costPrice <= 0 || !price || price <= 0) {
		return null;
	}
	
	const margin = ((price - costPrice) / price) * 100;
	return `+${margin.toFixed(0)}%`;
};
